import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';

/**
 * Basic regex to check if a string contains only numbers. If that's the case,
 * a phone number is going to be formatted manually. This is for scenarios
 * where a phone number has been entered, without the aid of a masked input
 * (e.g. search contacts screen for creating rooms)
 */
export const allNumbersRegex = /^\d+$/;

/**
 * Very basic regex just to detect if it's a phone number: only digits and
 * special characters +, -, (, ) and space, which are commonly present in
 * phone numbers
 */
export const basicPhoneRegex = /^[0-9+\-() ]+$/;

/**
 * Supported formats:
 * - Full phone number A: +1 201-888-8888
 * - Full phone number B: +1 201 888 8888
 * - Full phone number C: +1 201 888-8888
 * - Full phone number D: +1 2018888888
 * - Full phone number E: +12018888888
 * - Partial (no country code) A: 201-888-8888
 * - Partial (no country code) B: 201 888 8888
 * - Partial (no country code) C: 201 888-8888
 * - Partial (no country code) D: 201 8888888
 * - Partial (no country code) E: 2018888888
 * - Partial A (no country or area codes): 888-8888
 * - Partial B (no country or area codes): 888 8888
 * - Partial B (no country or area codes): 8888888
 */
export const phoneSearchRegex =
  /\+?(\d)?\s?(\(?\d{3}?\)?[\s-]?)?(\d{3}[\s-]?)(\d{4})/;

/**
 * Parse phone format. Examples:
 *   - 1 888-452-1505 => (888) 452-1505
 *   - 425-637-8900 ext 499 => (425) 637-8900
 *   - +50688888888 => +506 8888 8888
 * If masked number is required:
 *   - 1 888-452-1505 => (***) ***-1505
 *   - 425-637-8900 ext 499 => (***) ***-8900
 *   - +50688888888 => *** **** 8888
 */

export function formatPhoneNumber(
  phone: string | undefined,
  maskPhoneNumber?: boolean
): string {
  try {
    const parsedPhone = parsePhoneNumber(phone ?? '', 'US');
    // US numbers: national format
    // Non-US numbers: international format
    const phoneNumber = new AsYouType(parsedPhone?.country).input(
      `${
        parsedPhone?.country === 'US'
          ? parsedPhone?.nationalNumber
          : parsedPhone?.number
      }`
    );

    if (!maskPhoneNumber || phoneNumber.length <= 4) {
      return phoneNumber;
    }

    return (
      phoneNumber.slice(0, -4).replace(/^\+/, '').replace(/\d/g, '*') +
      phoneNumber.slice(-4)
    );
  } catch (e) {
    // Return unformatted phone number or empty string
    // @todo handle NOT_A_NUMBER, check isPossiblePhoneNumber
    return phone || '';
  }
}
